import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  ClickAwayListener,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Science as ScienceIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/auth/authContext';
import { trackEvent } from '../../utils/analytics';
import { ONBOARDING_EVENTS } from '../onboarding/onboardingEventNames';
import {
  dismissLearningLabDashboardPrompt,
} from './learningLabNavVisibility';
import { LearningLabNavPopover } from './LearningLabNavPopover';
import { useLearningLabNav } from './useLearningLabNav';

const iconButtonSx = {
  p: { xs: 1, sm: 1 },
  backgroundColor: 'rgba(0, 0, 0, 0.04)',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    transform: 'scale(1.05)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  transition: 'all 0.2s ease-in-out',
};

export const LearningLabNavControl: React.FC = () => {
  const theme = useTheme();
  const isMobileLab = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated } = useAuth();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);

  const {
    navContext,
    navState,
    showNav,
    iconDisabled,
    iconActive,
    ariaLabel,
    tooltip,
  } = useLearningLabNav();

  const trackNavEvent = useCallback(
    (action: string, extra?: Record<string, unknown>) => {
      trackEvent({
        action,
        category: 'onboarding',
        label: 'coordinator_learning_lab',
        custom_parameters: { source: 'header', ...extra },
      });
    },
    [],
  );

  const handleOpen = useCallback(() => {
    dismissLearningLabDashboardPrompt();
    trackNavEvent(ONBOARDING_EVENTS.LEARNING_LAB_NAV_OPEN, {
      viewport: isMobileLab ? 'mobile' : 'desktop',
    });
    if (isMobileLab) {
      setMobileSheetOpen(true);
      return;
    }
    setAnchorEl(buttonRef.current);
  }, [isMobileLab, trackNavEvent]);

  const handleClosePopover = useCallback(() => {
    setAnchorEl(null);
    buttonRef.current?.focus();
  }, []);

  const handleCloseMobileSheet = useCallback(() => {
    setMobileSheetOpen(false);
    buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!anchorEl) return;
    const handleScrollClose = (event: Event) => {
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('.MuiMenu-root, .MuiPopover-root, [role="menu"]')
      ) {
        return;
      }
      setAnchorEl(null);
    };
    window.addEventListener('scroll', handleScrollClose, { passive: true, capture: true });
    return () => {
      window.removeEventListener('scroll', handleScrollClose, true);
    };
  }, [anchorEl]);

  if (!isAuthenticated || !showNav) {
    return null;
  }

  const iconButtonDisabled = navState === 'loading';
  const showStaleBadge = navState === 'activeStale';

  const button = (
    <IconButton
      ref={buttonRef}
      id="learning-lab-nav-button"
      data-testid="header-learning-lab"
      onClick={handleOpen}
      disabled={iconButtonDisabled}
      color="inherit"
      aria-label={ariaLabel}
      aria-haspopup="true"
      aria-expanded={Boolean(anchorEl) || mobileSheetOpen}
      sx={{
        ...iconButtonSx,
        opacity: iconDisabled && !iconButtonDisabled ? 0.55 : 1,
        ...(iconActive
          ? {
              backgroundColor: 'rgba(2, 136, 209, 0.12)',
              borderColor: 'rgba(2, 136, 209, 0.35)',
            }
          : {}),
      }}
    >
      <Badge
        variant="dot"
        color="warning"
        invisible={!showStaleBadge}
        overlap="circular"
      >
        <ScienceIcon
          sx={{
            fontSize: { xs: 18, sm: 20 },
            color: iconActive ? 'info.main' : 'text.primary',
          }}
        />
      </Badge>
    </IconButton>
  );

  return (
    <Box data-tour-stable="learning-lab-nav" component="span" sx={{ display: 'inline-flex' }}>
      {tooltip ? (
        <Tooltip title={tooltip} arrow>
          <Box component="span">{button}</Box>
        </Tooltip>
      ) : (
        button
      )}

      <Popover
        open={Boolean(anchorEl) && !isMobileLab}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // Keep sticky AppBar pinned while the Learning Lab popover is open.
        disableScrollLock
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          },
        }}
      >
        <ClickAwayListener onClickAway={handleClosePopover}>
          <Box component="div" sx={{ outline: 'none' }}>
            <LearningLabNavPopover
              navState={navState}
              navContext={navContext}
              onClose={handleClosePopover}
            />
          </Box>
        </ClickAwayListener>
      </Popover>

      <Dialog
        open={mobileSheetOpen}
        onClose={handleCloseMobileSheet}
        fullWidth
        maxWidth="sm"
        data-testid="learning-lab-mobile-sheet"
      >
        <DialogTitle>Learning Lab practice</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Desktop is recommended for form-heavy practice steps, but you can enter
            and run missions on this device.
          </Typography>
          <LearningLabNavPopover
            navState={navState}
            navContext={navContext}
            onClose={handleCloseMobileSheet}
            showDesktopTip={false}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LearningLabNavControl;
